import { useFormField } from '@calendly/form';
import { useQueryParams } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { TagContainer, TagInput } from '@calendly/tags';
import classNames from 'classnames';
import { useState, useCallback, useRef, useEffect, forwardRef } from 'react';
import { buildInvitation } from 'features/invite_flow/utils';
import { useBooleanState } from 'hooks/useBooleanState';
import { isEnterKey, isSpaceKey, isTabKey, isCommaKey, isBackspaceKey } from 'services/keycodes';
import { TagView } from './tag_view';
import { RoleSelect } from '../../../add_users_modal/components/role_select';
import { useNewAdminModal } from '../../../add_users_modal/use-new-admin-modal';
import { DELIMITER_CHARS, DELIMITER_CHARS_REGEXP } from '../../../constants';
const flexWrapperIsInlinedWithRoleSelectCss = "f1nvcc2z";
const flexWrapperFocusCss = "fglo0xw";
const flexWrapperSingleRowCss = "f8v3acd";
const flexWrapperCss = "f1ujex84";
const FlexWrapper = forwardRef((props, ref) => {
  const {
    isInlinedWithRoleSelect,
    isFocused: focus,
    singleRow,
    as: AsComponent,
    className,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexWrapperCss, {
    [flexWrapperIsInlinedWithRoleSelectCss]: isInlinedWithRoleSelect,
    [flexWrapperFocusCss]: focus,
    [flexWrapperSingleRowCss]: singleRow
  }, className)} />;
});
const styledTagContainerFocusCss = "s6qc8q0";
const styledTagContainerSingleRowCss = "suvohyu";
const styledTagContainerIsInlinedWithRoleSelectCss = "spqysxu";
const styledTagContainerCss = "shm2w0g";
const StyledTagContainer = forwardRef((props, ref) => {
  const {
    singleRow,
    isInlinedWithRoleSelect,
    isFocused: focus,
    className,
    errors,
    ...restProps
  } = props;
  const error = errors && Boolean(errors.length);
  return <TagContainer ref={ref} {...restProps} error={error} className={classNames(styledTagContainerCss, {
    [styledTagContainerFocusCss]: focus,
    [styledTagContainerSingleRowCss]: singleRow,
    [styledTagContainerIsInlinedWithRoleSelectCss]: isInlinedWithRoleSelect
  }, className)} />;
});
export const Container = ({
  remove,
  push,
  onChangeOrRemove,
  singleRow = false,
  placeholderText = '',
  inputId = 'invitee_emails_input',
  inlineRoleSelect = false
}) => {
  const [isFocused, focus, blur] = useBooleanState(false);
  const [inputValue, setInputValue] = useState('');
  const tagInputRef = useRef(null);
  const [placeholder, setPlaceholder] = useState(placeholderText);
  const [{
    value: invitations
  }, _meta, {
    setTouched
  }] = useFormField({
    name: 'invitations'
  });
  const [{
    value: groupId
  }] = useFormField({
    name: 'groupId'
  });
  const isAdminFlagEnabled = useNewAdminModal();
  const useInlineRoleSelect = isAdminFlagEnabled && inlineRoleSelect;
  const queryParams = useQueryParams();
  useEffect(() => {
    if (invitations.length > 0) {
      setPlaceholder('');
    } else {
      setPlaceholder(placeholderText);
    }
  }, [invitations, placeholderText]);
  useEffect(() => {
    if (queryParams.prefillInviteForm) {
      push(buildInvitation(queryParams.prefillInviteForm, null));
    }
  }, [push, queryParams]);
  const handleFocus = useCallback(() => {
    focus();
  }, [focus]);
  const handleAdd = useCallback(() => {
    if (inputValue.length) {
      setTouched(true);
      setPlaceholder('');
      push(buildInvitation(inputValue, groupId));
    }
    setInputValue('');
  }, [push, inputValue, setTouched, groupId]);
  const handleAddOnBlur = useCallback(() => {
    if (inputValue.length) {
      push(buildInvitation(inputValue, groupId));
    }
    setInputValue('');
    blur();
  }, [push, blur, inputValue, groupId]);
  const handleBeforeInput = useCallback(event => {
    if (DELIMITER_CHARS.includes(event.data)) {
      handleAdd();
      event.preventDefault();
    }
  }, [handleAdd]);
  const handleKeyDown = useCallback(event => {
    if (isEnterKey(event) || isCommaKey(event) || isSpaceKey(event)) {
      handleAdd();
      event.preventDefault();
    } else if (isBackspaceKey(event)) {
      if (!inputValue.length && invitations.length) {
        remove(invitations.length - 1);
      }
    } else if (isTabKey(event)) {
      handleAdd();
    }
  }, [handleAdd, inputValue, invitations, remove]);
  const handlePaste = useCallback(event => {
    const pasteText = event.clipboardData.getData('Text');
    pasteText.split(DELIMITER_CHARS_REGEXP).filter(Boolean).map(email => buildInvitation(email.trim(), groupId)).forEach(push);
    event.preventDefault();
  }, [push, groupId]);
  const handleMouseDown = useCallback(() => {
    focus();
  }, [focus]);
  const onRemoveHandler = useCallback((name, index) => {
    onChangeOrRemove(name);
    remove(index);
  }, [onChangeOrRemove, remove]);
  return <FlexWrapper isInlinedWithRoleSelect={useInlineRoleSelect} isFocused={isFocused} singleRow={singleRow}>
      <StyledTagContainer ref={tagInputRef} onKeyDown={handleKeyDown} onInputChange={setInputValue} onBeforeInput={handleBeforeInput} onPaste={handlePaste} onInputFocus={handleFocus} onInputBlur={handleAddOnBlur} onMouseDown={handleMouseDown} inputValue={inputValue} inputAttributes={{
      name: 'invitations',
      type: 'email',
      id: `${inputId}`,
      'aria-expanded': true,
      'aria-controls': `${inputId}`,
      'aria-label': I18n.t('publisher.organization.invite_flow.add_emails_step.emails_input_field.field'),
      placeholder: `${placeholder}`
    }} singleRow={singleRow} isInlinedWithRoleSelect={useInlineRoleSelect} data-testid="invitee_emails">
        {invitations.map(({
        id
      }, index) => <TagView onChangeValue={onChangeOrRemove} index={index} key={id} name={`invitations.${index}.email`} onDelete={() => onRemoveHandler(`invitations.${index}.email`, index)} tagInputRef={tagInputRef} />)}
        <TagInput />
      </StyledTagContainer>
      {useInlineRoleSelect && <RoleSelect />}
    </FlexWrapper>;
};

require("./container.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./container.js");